import { getItemStat, Item } from "../models/common";
import { SpriteLoader } from "../sprite-loader";
import { COLORS, hexToPhaser } from "./colors";
import { getAmmoColor } from "../utils/item-utils";

export class WeaponUI {
  private scene: Phaser.Scene;
  private uiLayer: Phaser.GameObjects.Container;
  private container: Phaser.GameObjects.Container | null = null;
  private weaponSprite: Phaser.GameObjects.Sprite | null = null;
  private ammoBarBg: Phaser.GameObjects.Rectangle | null = null;
  private ammoBarFill: Phaser.GameObjects.Rectangle | null = null;
  private ammoText: Phaser.GameObjects.Text | null = null;
  private ammoBarHeight: number = 24;

  constructor(scene: Phaser.Scene, uiLayer: Phaser.GameObjects.Container) {
    this.scene = scene;
    this.uiLayer = uiLayer;
  }

  /**
   * Show the weapon UI in the upper left corner
   */
  show() {
    this.hide(); // Clean up any existing UI

    if (
      !window.player ||
      !window.player.data ||
      !window.player.data.equipment
    ) {
      return;
    }

    const weapon = window.player.data.equipment["Weapon"] as Item | undefined;

    if (!weapon || weapon.definition.equipType !== "Weapon") {
      return; // No weapon equipped, don't show anything
    }

    // Position in upper left corner
    const baseX = 20;
    const baseY = 16;
    const weaponSpriteSize = 48;
    const ammoBarWidth = 100;
    const spacing = 8;
    const marginY = 0;

    // Create weapon sprite
    this.weaponSprite = SpriteLoader.createSprite(
      this.scene,
      weapon.definition.spriteSheet,
      weapon.definition.spriteX,
      weapon.definition.spriteY,
      baseX + weaponSpriteSize / 2,
      baseY + weaponSpriteSize / 2,
      true, // fixed UI
    );
    this.weaponSprite.setDisplaySize(weaponSpriteSize, weaponSpriteSize);
    this.weaponSprite.setDepth(5000);

    // Get ammo info from weapon stats - prioritize currentAmmo property
    const currentAmmo = weapon.currentAmmo ?? 0;
    const maxAmmo = getItemStat(weapon, "ammo");
    const ammoPercent = maxAmmo > 0 ? Math.min(1, currentAmmo / maxAmmo) : 1;

    // Don't show ammo bar for weapons without ammo (melee weapons)
    const showAmmoBar = maxAmmo > 0;

    // Create ammo bar and text only if weapon uses ammo
    if (showAmmoBar) {
      // Create ammo bar background
      const ammoBarX = baseX + weaponSpriteSize + spacing;
      const ammoBarY = baseY + weaponSpriteSize / 2;

      this.ammoBarBg = this.scene.add.rectangle(
        ammoBarX,
        ammoBarY,
        ammoBarWidth,
        this.ammoBarHeight,
        hexToPhaser(COLORS.GRAY),
      );
      this.ammoBarBg.setOrigin(0, 0.5);
      this.ammoBarBg.setScrollFactor(0);
      this.ammoBarBg.setDepth(5000);
      this.ammoBarBg.setStrokeStyle(1, hexToPhaser(COLORS.WHITE));

      // Create ammo bar fill - color changes based on ammo level
      const fillWidth = (ammoBarWidth - 2) * ammoPercent;
      const fillColor = getAmmoColor(currentAmmo, maxAmmo);

      this.ammoBarFill = this.scene.add.rectangle(
        ammoBarX + 1 + fillWidth / 2,
        ammoBarY,
        fillWidth,
        this.ammoBarHeight - 2,
        hexToPhaser(fillColor),
      );
      this.ammoBarFill.setOrigin(0.5, 0.5);
      this.ammoBarFill.setScrollFactor(0);
      this.ammoBarFill.setDepth(5001);
      this.ammoBarFill.setVisible(ammoPercent > 0);

      // Create ammo text
      this.ammoText = this.scene.add.text(
        ammoBarX + ammoBarWidth / 2,
        ammoBarY,
        `${currentAmmo}/${maxAmmo}`,
        {
          fontSize: "16px",
          color: COLORS.WHITE,
          fontFamily: "Anton",
          align: "center",
          stroke: COLORS.BLACK,
          strokeThickness: 1,
        },
      );
      this.ammoText.setOrigin(0.5, 0.5);
      this.ammoText.setScrollFactor(0);
      this.ammoText.setDepth(5002);
    }

    // Create container with all elements
    const containerElements: Phaser.GameObjects.GameObject[] = [
      this.weaponSprite,
    ];
    if (this.ammoBarBg) containerElements.push(this.ammoBarBg);
    if (this.ammoBarFill) containerElements.push(this.ammoBarFill);
    if (this.ammoText) containerElements.push(this.ammoText);

    this.container = this.scene.add.container(0, marginY, containerElements);
    this.container.setScrollFactor(0);
    this.container.setDepth(5000);

    // Add container to UI layer so main camera ignores it
    this.uiLayer.add(this.container);
  }

  /**
   * Manually refresh the weapon UI - call this when weapon/ammo changes
   * Shows weapon UI in MainScene, and in BaseScene only on Raider tab.
   */
  refresh() {
    const sceneKey = this.scene.scene.key;
    let shouldShowWeaponUI = sceneKey === "MainScene";

    if (sceneKey === "BaseScene") {
      const baseScene = this.scene as Phaser.Scene & {
        currentTabIndex?: number;
      };
      shouldShowWeaponUI = baseScene.currentTabIndex === 0;
    }

    if (!shouldShowWeaponUI) {
      this.hide();
      return;
    }

    if (
      !window.player ||
      !window.player.data ||
      !window.player.data.equipment
    ) {
      this.hide();
      return;
    }

    const weapon = window.player.data.equipment["Weapon"] as Item | undefined;

    if (!weapon || weapon.definition.equipType !== "Weapon") {
      this.hide();
      return;
    }

    // Show UI if weapon exists but UI doesn't
    if (!this.container) {
      this.show();
      return;
    }

    // Update existing UI
    this.update();
  }

  /**
   * Update the weapon UI with current weapon and ammo data
   * Only updates existing UI - does not create new UI
   */
  update() {
    // Only update if UI is currently visible
    if (!this.container) {
      return;
    }

    if (
      !window.player ||
      !window.player.data ||
      !window.player.data.equipment
    ) {
      this.hide();
      return;
    }

    const weapon = window.player.data.equipment["Weapon"] as Item | undefined;

    if (!weapon || weapon.definition.equipType !== "Weapon") {
      this.hide();
      return;
    }

    // Update existing UI
    const currentAmmo = weapon.currentAmmo ?? 0;
    const maxAmmo = getItemStat(weapon, "ammo");
    const ammoPercent = maxAmmo > 0 ? Math.min(1, currentAmmo / maxAmmo) : 1;
    const showAmmoBar = maxAmmo > 0;

    // Update weapon sprite if changed
    if (this.weaponSprite) {
      // Check if weapon changed by comparing sprite coordinates
      const currentFrame = this.weaponSprite.frame.name;
      const typeColumns: Record<string, number> = {
        weapon: 10,
        armor: 10,
        medic: 2,
        ammo: 10,
        items: 16,
      };
      const columns = typeColumns[weapon.definition.spriteSheet] || 4;
      const expectedFrame =
        weapon.definition.spriteY * columns + weapon.definition.spriteX;

      if (currentFrame !== expectedFrame.toString()) {
        this.weaponSprite.setFrame(expectedFrame);
      }
    }

    // Update ammo bar fill only if weapon uses ammo
    if (showAmmoBar && this.ammoBarBg && this.ammoBarFill) {
      const ammoBarWidth = 100;
      const fillWidth = (ammoBarWidth - 2) * ammoPercent;

      // Update fill color based on ammo level
      const fillColor = getAmmoColor(currentAmmo, maxAmmo);

      this.ammoBarFill.setFillStyle(hexToPhaser(fillColor));
      this.ammoBarFill.setSize(fillWidth, this.ammoBarHeight - 2);
      this.ammoBarFill.setPosition(
        this.ammoBarBg.x + 1 + fillWidth / 2,
        this.ammoBarBg.y,
      );
      this.ammoBarFill.setVisible(ammoPercent > 0);
    }

    // Update ammo text only if weapon uses ammo
    if (showAmmoBar && this.ammoText) {
      this.ammoText.setText(`${currentAmmo}/${maxAmmo}`);
    }
  }

  /**
   * Hide and destroy the weapon UI
   */
  hide() {
    // Destroy the container first - this will automatically destroy all child elements
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }

    // Clear references to prevent memory leaks (elements are already destroyed by container)
    this.weaponSprite = null;
    this.ammoBarBg = null;
    this.ammoBarFill = null;
    this.ammoText = null;
  }

  /**
   * Set visibility of the weapon UI without destroying it
   */
  setVisible(visible: boolean) {
    if (this.container) {
      this.container.setVisible(visible);
    }
  }

  /**
   * Check if weapon UI is currently visible
   */
  isVisible(): boolean {
    return this.container !== null && this.container.visible;
  }

  /**
   * Get the width of the weapon UI for positioning other elements
   */
  getUIWidth(): number {
    if (!this.isVisible()) {
      return 0;
    }

    const weaponSpriteSize = 48;
    const ammoBarWidth = 100;
    const spacing = 8;

    // If weapon has ammo bars, include them in width calculation
    if (this.ammoBarBg) {
      return weaponSpriteSize + spacing + ammoBarWidth;
    } else {
      // Only weapon sprite for melee weapons
      return weaponSpriteSize;
    }
  }

  /**
   * Destroy the weapon UI component
   */
  destroy() {
    this.hide();
  }
}
